import React, { use, useCallback, useEffect, useMemo, useState } from "react";
import { StyleSheet, View } from "react-native";
import { useI18n } from "../../hooks/i18n-hooks";
import { useTheme } from "../../hooks/use-theme";
import VStack from "../../ui_atoms/VStack";
import Text from "../../components/Text";
import PostOnboardingImage from "../../assets/images/post-onboarding-image.svg";
import {
    AuthManagerContext,
    IS_INITIAL_WORKER_ONBOARDING,
    IS_POST_ONBOARDING_PREPARE_DISPLAYED,
} from "../../contexts/AuthManager";
import ScreenCard from "../ScreenCard";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useFocusEffect, useNavigation } from "@react-navigation/native";
import { useTracking } from "../../hooks/tracking";
import { usePostOnboardingData } from "../../hooks/use-postonboarding-data";
import { safeAsyncStorage } from "../../utils/safe-async-storage";
import Loading from "../../components/Loading";
import { useSafeAreaInsets } from "react-native-safe-area-context";

type ScreenParams = StackScreenProps<RootStackParamList>;

const PostOnboardingPrepare = () => {
    const i18n = useI18n();
    const { colors, gridSize } = useTheme();
    const styles = useStyles();
    const insets = useSafeAreaInsets();
    const navigator = useNavigation<ScreenParams["navigation"]>();
    const { user } = use(AuthManagerContext);
    const { trackEvent } = useTracking();
    const [isInitialWorkerOnboarding, setIsInitialWorkerOnboarding] = useState<boolean>();

    const next = useCallback(() => {
        navigator.replace("PostOnboardingProfileEditor");
    }, [navigator]);

    useFocusEffect(
        useCallback(() => {
            void (async () => {
                const isPostOnboardingPrepareDisplayed =
                    (await safeAsyncStorage.getItem(IS_POST_ONBOARDING_PREPARE_DISPLAYED)) ===
                    "true";
                const _isInitialWorkerOnboarding =
                    (await safeAsyncStorage.getItem(IS_INITIAL_WORKER_ONBOARDING)) === "true";

                if (!isPostOnboardingPrepareDisplayed && _isInitialWorkerOnboarding) {
                    navigator.replace("PostOnboardingProfileEditor");
                }
                setIsInitialWorkerOnboarding(_isInitialWorkerOnboarding);
            })();
        }, [navigator]),
    );

    const { missingInformations } = usePostOnboardingData();

    useEffect(() => {
        void trackEvent("signup_completed_worker");
    }, [trackEvent]);

    const translateKey = isInitialWorkerOnboarding ? "postOnboarding" : "postOnboarding_more";

    if (isInitialWorkerOnboarding === undefined) {
        return <Loading />;
    }

    return (
        <ScreenCard
            style={{ backgroundColor: colors.onPrimary, paddingTop: insets.top }}
            isContentContainerFlex
            buttons={[
                {
                    title: i18n.t(`screens.auth.postOnboarding.nextButton`),
                    onPress: next,
                },
            ]}
        >
            <VStack gap={1} h="2/3">
                <Text style={styles.title}>
                    {i18n.t(`screens.auth.${translateKey}.title`, {
                        name: user?.firstName,
                    })}
                </Text>
                {isInitialWorkerOnboarding && (
                    <Text style={styles.subTitle}>
                        {i18n.t(`screens.auth.postOnboarding.sub_title`)}
                    </Text>
                )}
                <View style={{ marginHorizontal: -gridSize }}>
                    <PostOnboardingImage width="100%" style={{ overflow: "visible" }} />
                </View>
                <Text style={styles.description}>
                    {i18n.t(`screens.auth.${translateKey}.description`, {
                        informations: missingInformations
                            .map(
                                (missingInfo) =>
                                    `• ${i18n.t(`screens.profile_editor.${missingInfo}.title`)}`,
                            )
                            .join("\n\n"),
                    })}
                </Text>
            </VStack>
        </ScreenCard>
    );
};

const useStyles = () => {
    const { fonts } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                title: {
                    ...fonts.titleLarge,
                    textAlign: "center",
                },
                subTitle: {
                    ...fonts.titleMedium,
                    textAlign: "center",
                },
                description: {
                    ...fonts.bodyMedium,
                    textAlign: "center",
                },
            }),
        [fonts.bodyMedium, fonts.titleLarge, fonts.titleMedium],
    );
};

export default PostOnboardingPrepare;
